/**
 * ReviewFunnelWidget — compact dashboard tile that shows Google-review
 * funnel health at a glance. Sits beside the CollectionEmailAuditWidget
 * in the admin dashboard header.
 *
 * Tile face: headline CTR% + tiny "N sent · M clicks · window" line.
 * Click to expand → per-source click breakdown (invoice / collection /
 * review-request / on-page CTAs) + a window selector (7 / 30 / 90 / 365
 * days). Data comes from GET /api/emails/review-funnel?days={n}.
 *
 * Admin-only, matches the failure/loading semantics of
 * CollectionEmailAuditWidget so partner/staff sessions gracefully hide it.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Star, RefreshCw, Loader2, ExternalLink } from "lucide-react";
import { API } from "../../App";
import { useDashboardWidgetPanel } from "../../lib/useDashboardWidgetPanel";

const WINDOW_OPTIONS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1y", days: 365 },
];

// The three actual outbound sending channels — each has both a
// "sent count" AND a "clicks-by-source" bucket, so we can compute a
// per-channel CTR. Everything else (invoice_page, balance_success,
// unknown) is a CLICK source only (no corresponding send count) and
// gets rendered as a separate "other click sources" list.
const SEND_CHANNELS = [
  { key: "collection", label: "Collection email" },
  { key: "invoice", label: "Invoice email" },
  { key: "review_request", label: "3-day follow-up" },
];

// Non-sending click sources — page-embedded CTAs + legacy pre-tracking.
// These have clicks but no `sent` count, so a per-channel rate is
// meaningless. Rendered as a plain count list.
const OTHER_CLICK_SOURCES = [
  { key: "invoice_page", label: "Invoice page" },
  { key: "balance_success", label: "Balance-success page" },
  { key: "unknown", label: "Legacy (pre-tracking)" },
];

const ReviewFunnelWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { open, toggle, panelRef, tileRef } = useDashboardWidgetPanel("review-funnel");
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);

  const load = useCallback(async (windowDays) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/emails/review-funnel`, {
        params: { days: windowDays },
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        // Non-admin session — hide entirely.
        setData({ __hidden: true });
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [load, days]);

  if (!data || data.__hidden) return null;

  const ctr = data.ctr_pct ?? 0;
  const sent = data.total_review_ctas_sent ?? 0;
  const clicks = data.total_clicks ?? 0;

  // Empty-window helper text so a fresh install / low-volume day doesn't
  // just show "0% / 0 sent / 0 clicks" with no context.
  const emptyState = sent === 0 && clicks === 0;

  return (
    <div className="relative" data-testid="review-funnel-widget">
      <button
        ref={tileRef}
        type="button"
        onClick={toggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20"
        title={`Review CTR over last ${days} days`}
        data-testid="review-funnel-tile-btn"
      >
        <Star className="w-4 h-4" />
        <span data-testid="review-funnel-headline">
          {emptyState ? "No review data yet" : `${ctr}% CTR`}
        </span>
        <span className="text-xs text-amber-200/70 hidden sm:inline">
          · {sent} sent · {clicks} clicks · {days}d
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none" aria-hidden />
          <div
            ref={panelRef}
            className="fixed left-2 right-2 top-24 max-w-md mx-auto z-50 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:mx-0 w-auto sm:w-[340px] bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-4"
            data-testid="review-funnel-panel"
          >
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-semibold text-sm">Review funnel</h4>
            <button
              type="button"
              onClick={() => load(days)}
              disabled={loading}
              className="text-slate-400 hover:text-white p-1 disabled:opacity-50"
              title="Refresh"
              data-testid="review-funnel-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>

          {/* Window selector */}
          <div className="flex gap-1 mb-3">
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                type="button"
                onClick={() => setDays(opt.days)}
                className={[
                  "flex-1 text-xs py-1.5 rounded border transition-colors",
                  days === opt.days
                    ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                    : "bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200",
                ].join(" ")}
                data-testid={`review-funnel-window-${opt.days}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {error ? (
            <p className="text-rose-400 text-xs">Failed to load: {error}</p>
          ) : (
            <>
              {/* Headline metrics */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <Metric label="CTR" value={`${ctr}%`} testId="review-funnel-ctr" tone="amber" />
                <Metric label="Sent" value={sent} testId="review-funnel-sent" />
                <Metric label="Clicks" value={clicks} testId="review-funnel-clicks" />
              </div>

              {/* Per-channel performance — sent / clicked / rate.
                  Replaces the earlier two separate "sent" and "clicks-
                  by-source" lists for the three real sending channels,
                  because the per-channel rate is the actually useful
                  metric here (was previously two separate lists you had
                  to eye-track between). Only the three genuine sending
                  channels appear — invoice_page / balance_success /
                  unknown are click sources without a corresponding
                  send count, and are rendered separately below. */}
              <SectionTitle>Per-channel performance</SectionTitle>
              <div className="space-y-1" data-testid="review-funnel-per-channel">
                {SEND_CHANNELS.map(({ key, label }) => {
                  const s = (data.emails_sent || {})[key] ?? 0;
                  const c = (data.clicks_by_source || {})[key] ?? 0;
                  // Guard against divide-by-zero: 0-sent → "—" rate,
                  // never NaN and never a misleading 0%.
                  const rate = s > 0 ? `${Math.round((c / s) * 100)}%` : "—";
                  return (
                    <div
                      key={key}
                      className="flex justify-between text-xs text-slate-300"
                      data-testid={`review-funnel-channel-${key}`}
                    >
                      <span>{label}</span>
                      <span className="font-mono text-slate-100">
                        <span data-testid={`review-funnel-channel-${key}-sent`}>{s}</span> sent
                        <span className="text-slate-500"> · </span>
                        <span data-testid={`review-funnel-channel-${key}-clicked`}>{c}</span> clicked
                        <span className="text-slate-500"> · </span>
                        <span
                          className={s > 0 ? "text-amber-300" : "text-slate-500"}
                          data-testid={`review-funnel-channel-${key}-rate`}
                        >
                          {rate}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Other click sources — non-sending buckets. Includes an
                  "Untracked" synthetic row when total_clicks exceeds the
                  sum of all attributed source buckets: those are legacy
                  clicks whose bookings had `review_link_clicked_at` set
                  but no `review_click_sources` sub-doc populated (pre-
                  source-tracking history). Without this we'd silently
                  hide those clicks and the widget would say "no clicks
                  yet" even though total_clicks > 0. Empty-state check
                  below correctly considers total_clicks + this synthetic
                  bucket, not just the six named sources. */}
              <div className="mt-3">
                <SectionTitle>Other click sources</SectionTitle>
                {(() => {
                  const src = data.clicks_by_source || {};
                  const otherRows = OTHER_CLICK_SOURCES
                    .map(({ key, label }) => ({ key, label, count: src[key] ?? 0 }))
                    .filter((r) => r.count > 0);
                  // Untracked = total_clicks - every attributed bucket
                  // (both send-channel and other-source). Clamp at 0 in
                  // case backend numbers ever go negative (defensive —
                  // aggregation shouldn't but a floor here beats a
                  // "-3 legacy clicks" UI regression).
                  const totalClicks = data.total_clicks ?? 0;
                  const attributed = Object.values(src).reduce(
                    (acc, v) => acc + (Number(v) || 0),
                    0,
                  );
                  const untracked = Math.max(0, totalClicks - attributed);
                  if (untracked > 0) {
                    otherRows.push({
                      key: "untracked_legacy",
                      label: "Untracked (legacy)",
                      count: untracked,
                    });
                  }
                  if (otherRows.length === 0) {
                    // Only show empty state when there truly are no
                    // clicks anywhere in this window — including the
                    // untracked delta. Previously the widget said "no
                    // clicks yet" whenever the three sending channels
                    // had all-zero click buckets, silently hiding both
                    // page CTA clicks and legacy untracked clicks.
                    return (
                      <p
                        className="text-slate-500 text-xs"
                        data-testid="review-funnel-other-empty"
                      >
                        {totalClicks > 0
                          ? "All clicks came via the sending channels above."
                          : "No clicks yet in this window."}
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-1">
                      {otherRows.map((r) => (
                        <div
                          key={r.key}
                          className="flex justify-between text-xs text-slate-300"
                          data-testid={`review-funnel-other-${r.key}`}
                        >
                          <span>{r.label}</span>
                          <span className="font-mono text-slate-100">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {data.unique_customers_clicked ?? 0} unique customers clicked
                </span>
                <a
                  href={`${API}/emails/review-stats`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-slate-400 hover:text-slate-200"
                  data-testid="review-funnel-raw-json-link"
                >
                  raw <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </>
          )}
        </div>
        </>
      )}
    </div>
  );
};

const Metric = ({ label, value, testId, tone }) => (
  <div
    className={[
      "bg-slate-800 rounded p-2 text-center border",
      tone === "amber" ? "border-amber-500/30" : "border-slate-700",
    ].join(" ")}
    data-testid={testId}
  >
    <div className={tone === "amber" ? "text-amber-300 font-bold text-lg leading-none" : "text-white font-bold text-lg leading-none"}>
      {value}
    </div>
    <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-1">{label}</div>
  </div>
);

const SectionTitle = ({ children }) => (
  <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{children}</p>
);

export default ReviewFunnelWidget;
